import { useState, useCallback } from 'react';
import type { SortingState } from '@tanstack/react-table';

export type Density = 'compact' | 'normal' | 'comfortable';

export interface DataTablePrefs {
  visibleColumns: string[];
  density: Density;
  sorting: SortingState;
}

const STORAGE_PREFIX = 'datatable-prefs:';

function load(key: string, defaultVisibleColumns: string[]): DataTablePrefs {
  try {
    const raw = localStorage.getItem(STORAGE_PREFIX + key);
    if (raw) {
      const parsed = JSON.parse(raw);
      return {
        visibleColumns: parsed.visibleColumns ?? defaultVisibleColumns,
        density: parsed.density ?? 'normal',
        sorting: parsed.sorting ?? [],
      };
    }
  } catch { /* ignore */ }
  return { visibleColumns: defaultVisibleColumns, density: 'normal', sorting: [] };
}

function save(key: string, prefs: DataTablePrefs) {
  try {
    localStorage.setItem(STORAGE_PREFIX + key, JSON.stringify(prefs));
  } catch { /* ignore */ }
}

export function useDataTablePrefs(storageKey: string, defaultVisibleColumns: string[]) {
  const [prefs, setPrefs] = useState<DataTablePrefs>(() => load(storageKey, defaultVisibleColumns));

  const update = useCallback((next: DataTablePrefs) => {
    setPrefs(next);
    save(storageKey, next);
  }, [storageKey]);

  const toggleColumn = useCallback((id: string) => {
    setPrefs(prev => {
      const cols = prev.visibleColumns.includes(id)
        ? prev.visibleColumns.filter(c => c !== id)
        : [...prev.visibleColumns, id];
      const next = { ...prev, visibleColumns: cols };
      save(storageKey, next);
      return next;
    });
  }, [storageKey]);

  const setDensity = useCallback((d: Density) => {
    setPrefs(prev => {
      const next = { ...prev, density: d };
      save(storageKey, next);
      return next;
    });
  }, [storageKey]);

  const setSorting = useCallback((s: SortingState) => {
    setPrefs(prev => {
      const next = { ...prev, sorting: s };
      save(storageKey, next);
      return next;
    });
  }, [storageKey]);

  return { prefs, toggleColumn, setDensity, setSorting };
}
